import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "warehouses";

export interface Warehouse {
	id: string;
	code: string;
	name: string;
	location: string | null;
	street: string | null;
	city: string | null;
	country: string | null;
	isActive: boolean;
	sapWhsCode: string | null;
	lastSyncedAt: string | null;
	syncStatus: string | null;
	createdAt: string;
	updatedAt: string;
}

export function useWarehouseList(options?: {
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			return api.get<{ items: Warehouse[]; total: number }>(`/api/warehouses?${params.toString()}`);
		},
	});
}

export function useWarehouseDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Warehouse>(`/api/warehouses/${id}`),
		enabled: !!id,
	});
}

export interface WarehouseStockRow {
	id: string;
	stockItemId: string;
	code: string;
	name: string;
	category: string | null;
	uom: string | null;
	currentStock: string | null;
	reservedAmount: string | null;
	onOrderAmount: string | null;
	warehouseCode?: string;
	warehouseName?: string;
}

export interface WarehouseStocksResult {
	items: WarehouseStockRow[];
	total: number;
	totalStock: string;
}

export function useWarehouseStocks(
	warehouseId: string,
	options?: {
		search?: string | undefined;
		category?: string | undefined;
		page?: number;
		limit?: number;
	},
) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "stocks", warehouseId, options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.search) params.set("search", options.search);
			if (options?.category) params.set("category", options.category);
			return api.get<WarehouseStocksResult>(
				`/api/warehouses/${warehouseId}/stocks?${params.toString()}`,
			);
		},
		enabled: !!warehouseId,
	});
}

export function useWarehouseStockSearch(options?: {
	search?: string | undefined;
	category?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	const search = options?.search?.trim() ?? "";
	return useQuery({
		queryKey: [KEY, "stock-search", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			params.set("search", search);
			if (options?.category) params.set("category", options.category);
			return api.get<WarehouseStocksResult>(`/api/warehouses/stock-search?${params.toString()}`);
		},
		enabled: search.length > 0,
	});
}
